import React from 'react';
import { motion } from 'framer-motion';
interface ColorPair {
  glass: string;
  cap: string;
}
const BottleSVG: React.FC<ColorPair> = ({
  glass,
  cap
}) => <svg viewBox="0 0 40 100" className="w-full h-full" data-id="element-40">
    <rect x="14" y="2" width="12" height="10" rx="1" fill={cap} data-id="element-41" />
    <rect x="15" y="12" width="10" height="14" fill={glass} opacity="0.9" data-id="element-42" />
    <path d="M 15 26 Q 8 30 8 40 L 8 92 Q 8 96 12 96 L 28 96 Q 32 96 32 92 L 32 40 Q 32 30 25 26 Z" fill={glass} data-id="element-43" />
    <path d="M 11 35 Q 10 50 11 85" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" data-id="element-44" />
    <rect x="11" y="55" width="18" height="22" fill="rgba(255,255,255,0.15)" data-id="element-45" />
    <rect x="14" y="62" width="12" height="2" fill="rgba(255,255,255,0.3)" data-id="element-46" />
    <rect x="14" y="68" width="8" height="1.5" fill="rgba(255,255,255,0.3)" data-id="element-47" />
  </svg>;
const CrateSVG: React.FC<ColorPair> = ({
  glass: bodyColor,
  cap: bottleColor
}) => <svg viewBox="0 0 80 100" className="w-full h-full" data-id="element-48">
    {/* Crate body */}
    <rect x="4" y="40" width="72" height="50" fill={bodyColor} stroke="rgba(0,0,0,0.2)" strokeWidth="1" rx="2" data-id="element-49" />
    <rect x="4" y="40" width="72" height="6" fill="rgba(0,0,0,0.15)" data-id="element-50" />
    {/* Bottles peeking out the top */}
    {[8, 22, 36, 50, 64].map((x, i) => <g key={i} data-id="element-51">
        <rect x={x} y="20" width="8" height="25" fill={bottleColor} rx="0.5" data-id="element-52" />
        <rect x={x + 2} y="14" width="4" height="8" fill={bottleColor} data-id="element-53" />
        <rect x={x + 2} y="10" width="4" height="5" fill="rgba(0,0,0,0.5)" data-id="element-54" />
      </g>)}
    {/* Front label */}
    <rect x="22" y="62" width="36" height="14" fill="rgba(255,255,255,0.85)" rx="1" data-id="element-55" />
    <rect x="26" y="66" width="28" height="2" fill={bodyColor} data-id="element-56" />
    <rect x="26" y="70" width="20" height="1.5" fill={bodyColor} opacity="0.6" data-id="element-57" />
    {/* Handles */}
    <rect x="6" y="55" width="2" height="10" fill="rgba(0,0,0,0.25)" data-id="element-58" />
    <rect x="72" y="55" width="2" height="10" fill="rgba(0,0,0,0.25)" data-id="element-59" />
  </svg>;
const PalletSVG: React.FC<ColorPair> = ({
  glass: woodColor
}) => <svg viewBox="0 0 120 80" className="w-full h-full" data-id="element-60">
    {/* Top deck planks */}
    <rect x="2" y="20" width="116" height="8" fill={woodColor} stroke="rgba(0,0,0,0.25)" strokeWidth="0.8" rx="1" data-id="element-61" />
    <rect x="2" y="32" width="116" height="8" fill={woodColor} stroke="rgba(0,0,0,0.25)" strokeWidth="0.8" rx="1" data-id="element-62" />
    <rect x="2" y="44" width="116" height="8" fill={woodColor} stroke="rgba(0,0,0,0.25)" strokeWidth="0.8" rx="1" data-id="element-63" />
    {/* Wood grain lines */}
    <line x1="20" y1="24" x2="100" y2="24" stroke="rgba(0,0,0,0.15)" strokeWidth="0.5" data-id="element-64" />
    <line x1="20" y1="36" x2="100" y2="36" stroke="rgba(0,0,0,0.15)" strokeWidth="0.5" data-id="element-65" />
    <line x1="20" y1="48" x2="100" y2="48" stroke="rgba(0,0,0,0.15)" strokeWidth="0.5" data-id="element-66" />
    {/* Support blocks */}
    <rect x="6" y="56" width="14" height="14" fill={woodColor} stroke="rgba(0,0,0,0.3)" strokeWidth="0.8" data-id="element-67" />
    <rect x="53" y="56" width="14" height="14" fill={woodColor} stroke="rgba(0,0,0,0.3)" strokeWidth="0.8" data-id="element-68" />
    <rect x="100" y="56" width="14" height="14" fill={woodColor} stroke="rgba(0,0,0,0.3)" strokeWidth="0.8" data-id="element-69" />
    {/* Bottom stringer */}
    <rect x="2" y="68" width="116" height="6" fill={woodColor} stroke="rgba(0,0,0,0.25)" strokeWidth="0.8" rx="1" data-id="element-70" />
    {/* Stamp/marking */}
    <rect x="74" y="22" width="20" height="6" fill="rgba(0,0,0,0.12)" rx="0.5" data-id="element-71" />
  </svg>;
type Asset = {
  kind: 'bottle';
  w: string;
  colors: ColorPair;
} | {
  kind: 'crate';
  w: string;
  colors: ColorPair;
} | {
  kind: 'pallet';
  w: string;
  colors: ColorPair;
};
const assets: Asset[] = [{
  kind: 'bottle',
  w: 'w-12 md:w-16',
  colors: {
    glass: '#1a3a2e',
    cap: '#e0a73d'
  }
}, {
  kind: 'crate',
  w: 'w-20 md:w-24',
  colors: {
    glass: '#c97a4a',
    cap: '#3aa856'
  }
}, {
  kind: 'bottle',
  w: 'w-12 md:w-16',
  colors: {
    glass: '#3aa856',
    cap: '#0f3d2e'
  }
}, {
  kind: 'pallet',
  w: 'w-28 md:w-32',
  colors: {
    glass: '#a8855c',
    cap: '#a8855c'
  }
}, {
  kind: 'bottle',
  w: 'w-12 md:w-16',
  colors: {
    glass: '#2d8fa8',
    cap: '#1c1c1c'
  }
}, {
  kind: 'crate',
  w: 'w-20 md:w-24',
  colors: {
    glass: '#3aa856',
    cap: '#e0a73d'
  }
}, {
  kind: 'bottle',
  w: 'w-12 md:w-16',
  colors: {
    glass: '#c97a4a',
    cap: '#1c1c1c'
  }
}, {
  kind: 'pallet',
  w: 'w-28 md:w-32',
  colors: {
    glass: '#8a6b48',
    cap: '#8a6b48'
  }
}, {
  kind: 'bottle',
  w: 'w-12 md:w-16',
  colors: {
    glass: '#e0a73d',
    cap: '#1a3a2e'
  }
}, {
  kind: 'crate',
  w: 'w-20 md:w-24',
  colors: {
    glass: '#2d8fa8',
    cap: '#faf8f3'
  }
}];
const renderAsset = (a: Asset) => {
  if (a.kind === 'bottle') return <BottleSVG {...a.colors} data-id="element-72" />;
  if (a.kind === 'crate') return <CrateSVG {...a.colors} data-id="element-73" />;
  return <PalletSVG {...a.colors} data-id="element-74" />;
};
export const BottleStrip: React.FC = () => {
  const allAssets = [...assets, ...assets, ...assets];
  return <div className="relative w-full overflow-hidden py-8 bg-primary border-y border-leaf/20" data-id="element-75">
      <div className="absolute inset-0 gloss-dark opacity-50" data-id="element-76" />

      <motion.div className="flex gap-10 items-end relative z-10" animate={{
      x: ['0%', '-33.333%']
    }} transition={{
      duration: 40,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-77">
        {allAssets.map((a, i) => <motion.div key={i} className={`shrink-0 ${a.w} h-28 md:h-36 flex items-end justify-center`} animate={{
        y: [0, -6, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        delay: i * 0.15,
        ease: 'easeInOut'
      }} data-id="element-78">
            {renderAsset(a)}
          </motion.div>)}
      </motion.div>

      {/* Edge fades */}
      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-primary to-transparent z-20" data-id="element-79" />
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-primary to-transparent z-20" data-id="element-80" />
    </div>;
};